import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Job, StateSchema, formatRelativeTime, renderHeadline } from '../services/servicesShared';

const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
};

/**
 * The job's first-class state: the headline, the typed fields the version's
 * state_schema declares, and the job's own free-form `details` behind a
 * disclosure (OWNED_JOBS_PLAN.md §1 Service state_schema).
 */
export const JobStateCard: React.FC<{ job: Job; schema: Partial<StateSchema> }> = ({ job, schema }) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const fields = schema?.fields || {};
    const fieldNames = Object.keys(fields);
    const extraStateKeys = Object.keys(job.state || {}).filter((key) => !fields[key]);
    const headline = job.headline || renderHeadline(schema?.headline, job.state || {});
    const detailKeys = Object.keys(job.details || {});

    return (
        <Card>
            <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">State</div>
                        <div className="text-2xl font-semibold mt-1 break-words">
                            {headline || <span className="text-muted-foreground italic text-base">No headline yet — the job sets it on its first wake.</span>}
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {job.state_updated_at ? `updated ${formatRelativeTime(job.state_updated_at)}` : ''}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {fieldNames.length > 0 || extraStateKeys.length > 0 ? (
                    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                        {fieldNames.map((name) => (
                            <div key={name} className="min-w-0">
                                <dt className="text-xs text-muted-foreground font-mono truncate" title={fields[name].description || name}>
                                    {name}
                                </dt>
                                <dd className="text-sm font-medium break-words">{formatValue(job.state?.[name])}</dd>
                                {fields[name].description && (
                                    <dd className="text-xs text-muted-foreground truncate">{fields[name].description}</dd>
                                )}
                            </div>
                        ))}
                        {extraStateKeys.map((name) => (
                            <div key={name} className="min-w-0">
                                <dt className="text-xs text-muted-foreground font-mono truncate">{name}</dt>
                                <dd className="text-sm font-medium break-words">{formatValue(job.state?.[name])}</dd>
                            </div>
                        ))}
                    </dl>
                ) : (
                    <p className="text-sm text-muted-foreground">This service declares no typed state fields.</p>
                )}

                <div>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setDetailsOpen((open) => !open)}
                    >
                        {detailsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        Details ({detailKeys.length} {detailKeys.length === 1 ? 'key' : 'keys'}) — the job's own working state
                    </button>
                    {detailsOpen && (
                        <pre className="mt-2 max-h-80 overflow-auto rounded bg-muted p-3 font-mono text-[11px] leading-snug whitespace-pre-wrap break-all">
                            {detailKeys.length ? JSON.stringify(job.details, null, 2) : '{}'}
                        </pre>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default JobStateCard;
